import { CmsAppearanceHint } from "@/components/cms/theme/CmsAppearanceHint";
import { CmsColorPickerField } from "@/components/cms/theme/CmsColorPickerField";
import {
  CMS_PALETTE_CONTROL_ROW_CLASS,
  CMS_PALETTE_FIELD_LABEL_CLASS,
  CMS_PALETTE_HEX_INPUT_CLASS,
  CMS_PALETTE_TEXT_INPUT_CLASS,
} from "@/components/cms/theme/cms-color-input-styles";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { isValidHex } from "@/lib/cms/cms-theme.constants";
import { cn } from "@/lib/utils";
import { Plus, Trash2 } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

export type PaletteColorDraft = {
  id?: string;
  name: string;
  hex: string;
  sortOrder: number;
};

type Props = {
  colors: PaletteColorDraft[];
  onChange: (colors: PaletteColorDraft[]) => void;
};

function uniqueDefaultColorName(
  colors: PaletteColorDraft[],
  baseName: string,
): string {
  const taken = new Set(colors.map((c) => c.name.trim().toLowerCase()));
  if (!taken.has(baseName.trim().toLowerCase())) return baseName;
  let n = 2;
  while (taken.has(`${baseName} ${n}`.trim().toLowerCase())) n += 1;
  return `${baseName} ${n}`;
}

export function ColorPaletteManager({ colors, onChange }: Props) {
  const { t } = useTranslation();
  const rowRefs = useRef<Record<number, HTMLLIElement | null>>({});

  const updateAt = (index: number, patch: Partial<PaletteColorDraft>) => {
    const next = colors.map((c, i) => (i === index ? { ...c, ...patch } : c));
    onChange(next);
  };

  const removeAt = (index: number) => {
    onChange(
      colors
        .filter((_, i) => i !== index)
        .map((color, nextIndex) => ({ ...color, sortOrder: nextIndex })),
    );
  };

  const addColor = () => {
    onChange([
      ...colors,
      {
        name: uniqueDefaultColorName(
          colors,
          t("cms.appearance.palette.newColorName"),
        ),
        hex: "#000000",
        sortOrder: colors.length,
      },
    ]);
  };

  const scrollToRow = (index: number) => {
    rowRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  };

  if (colors.length === 0) {
    return (
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-slate-800">
          {t("cms.appearance.palette.title")}
        </h3>
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/80 p-6 text-center space-y-4">
          <p className="text-sm font-medium text-slate-700">
            {t("cms.appearance.palette.emptyTitle")}
          </p>
          <CmsAppearanceHint>{t("cms.appearance.palette.emptyHint")}</CmsAppearanceHint>
          <Button type="button" variant="default" onClick={addColor}>
            <Plus className="h-4 w-4 mr-2" />
            {t("cms.appearance.palette.addFirst")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-800">
          {t("cms.appearance.palette.title")}
        </h3>
        <Button type="button" variant="outline" size="sm" onClick={addColor}>
          <Plus className="h-4 w-4 mr-1" />
          {t("cms.appearance.palette.add")}
        </Button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
          {t("cms.appearance.palette.preview")}
        </p>
        <div className="flex flex-wrap gap-2">
          {colors.map((color, index) => (
            <button
              key={color.id ?? `preview-${index}`}
              type="button"
              title={color.name}
              onClick={() => scrollToRow(index)}
              className="h-10 w-10 shrink-0 rounded-md border border-slate-200 transition-transform hover:scale-105 hover:ring-2 hover:ring-slate-300"
              style={{
                backgroundColor: isValidHex(color.hex) ? color.hex : "#000000",
              }}
            />
          ))}
        </div>
      </div>

      <ul className="space-y-3 pl-0">
        {colors.map((color, index) => (
          <li
            key={color.id ?? `draft-${index}`}
            ref={(el) => {
              rowRefs.current[index] = el;
            }}
            className="flex items-end gap-3 rounded-lg border border-slate-200 bg-white p-3"
          >
            <div className="grid min-w-0 flex-1 grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
              <div className="flex min-w-0 flex-col gap-1.5">
                <Label className={CMS_PALETTE_FIELD_LABEL_CLASS}>
                  {t("cms.appearance.palette.name")}
                </Label>
                <input
                  type="text"
                  value={color.name}
                  onChange={(e) => updateAt(index, { name: e.target.value })}
                  className={CMS_PALETTE_TEXT_INPUT_CLASS}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className={CMS_PALETTE_FIELD_LABEL_CLASS}>
                  {t("cms.appearance.palette.hex")}
                </Label>
                <div className={CMS_PALETTE_CONTROL_ROW_CLASS}>
                  <CmsColorPickerField
                    value={isValidHex(color.hex) ? color.hex : "#000000"}
                    aria-label={t("cms.appearance.palette.pickColor")}
                    onCommit={(hex) => updateAt(index, { hex })}
                  />
                  <input
                    type="text"
                    value={color.hex}
                    onChange={(e) => updateAt(index, { hex: e.target.value })}
                    onBlur={() => {
                      const hex = color.hex.trim().toUpperCase();
                      if (isValidHex(hex)) updateAt(index, { hex });
                    }}
                    className={CMS_PALETTE_HEX_INPUT_CLASS}
                    placeholder="#RRGGBB"
                    spellCheck={false}
                  />
                </div>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn("h-9 w-9 shrink-0")}
              onClick={() => removeAt(index)}
              aria-label={t("cms.appearance.palette.remove")}
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
